/**
 * Service Plans Repository
 * 플랜 정보 조회 (읽기 전용)
 */

import { API_BASE_URL } from '../../../config/api';

export interface PlanLimits {
  max_students: number;
  max_gates: number;
  max_branches: number;
}

export interface ServicePlan {
  id: string;
  name: string;
  display_name: string;
  price: number;
  billing_cycle: string;
  description: string;
  features: string[];
  limits: PlanLimits;
  is_active: boolean | number; // DB에서 0/1로 올 수 있음
  sort_order: number;
  // 알림톡 관련 필드
  alimtalk_unit_price: number;
  alimtalk_free_quota: number;
}

export interface ServicePlansResponse {
  success: boolean;
  plans: ServicePlan[];
}

export interface PlanCreateRequest {
  name: string;
  display_name: string;
  price: number;
  billing_cycle: string;
  description: string;
  features: string[];
  limits: PlanLimits;
  is_active: boolean;
  sort_order: number;
  // 알림톡 관련 필드
  alimtalk_unit_price?: number;
  alimtalk_free_quota?: number;
}

export interface PlanUpdateRequest {
  display_name?: string;
  price?: number;
  description?: string;
  features?: string[];
  limits?: PlanLimits;
  // 알림톡 관련 필드
  alimtalk_unit_price?: number;
  alimtalk_free_quota?: number;
}

export interface SinglePlanResponse {
  success: boolean;
  message: string;
  plan?: ServicePlan;
}

export interface DeletePlanResponse {
  success: boolean;
  message: string;
}

export class ServicePlansRepository {
  private baseUrl: string;

  constructor() {
    // 끝에 슬래시 없이 설정 (이중 슬래시 방지)
    this.baseUrl = `${API_BASE_URL}/api/v1/admin/service-plans`;
  }

  /**
   * 모든 플랜 조회 (관리자 전용)
   */
  async getAllPlans(token: string): Promise<ServicePlansResponse> {
    const response = await fetch(`${this.baseUrl}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '플랜 조회 실패');
    }

    return response.json();
  }

  /**
   * 새 플랜 생성 (관리자 전용)
   */
  async createPlan(token: string, data: PlanCreateRequest): Promise<SinglePlanResponse> {
    const response = await fetch(`${this.baseUrl}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '플랜 생성 실패');
    }

    return response.json();
  }

  /**
   * 플랜 수정 (관리자 전용)
   */
  async updatePlan(token: string, planId: string, data: PlanUpdateRequest): Promise<SinglePlanResponse> {
    const response = await fetch(`${this.baseUrl}/${planId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '플랜 수정 실패');
    }

    return response.json();
  }

  /**
   * 플랜 삭제 (관리자 전용)
   */
  async deletePlan(token: string, planId: string): Promise<DeletePlanResponse> {
    const response = await fetch(`${this.baseUrl}/${planId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '플랜 삭제 실패');
    }

    return response.json();
  }
}
